/**
 * computeCoveredOutline.ts
 * ------------------------------------------------------------
 * Computes which parts of a rectangle’s outline are visible
 * (solid) and which parts are covered (dashed) by other rectangles.
 *
 * Given a base rectangle and a list of cover rectangles, this
 * function returns the outline as a set of line segments suitable
 * for rendering — solid for uncovered portions, dashed for covered
 * ones. Works with axis-aligned rectangles.
 *
 * Example:
 *   const { solid, dashed } = computeCoveredOutline(rect, covers);
 *
 * Author: Michael Gähwiler (2025)
 * License: MIT
 */


/** Axis-aligned rectangle */
export interface Rect {
    x: number;      // left
    y: number;      // top
    w: number;      // width  (>= 0)
    h: number;      // height (>= 0)
}

/** 2D line segment */
export interface Segment {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

/** Result segments along the outline of `rect` */
export interface OutlineSegments {
    solid: Segment[];   // visible parts
    dashed: Segment[];  // covered parts
}

/**
 * Compute the outline (solid vs dashed) of `rect` given cover rectangles.
 * A boundary point is considered "covered" if any cover rectangle spans the boundary's axis
 * (same x for vertical edges, same y for horizontal edges) and overlaps along the edge's run.
 *
 * Assumptions:
 * - All rectangles are axis-aligned.
 * - Coverage is purely geometric (z-order: covers are on top of `rect`).
 * - Edges are infinitely thin; touching (inclusive) counts as coverage.
 */
export function computeCoveredOutline(rect: Rect, covers: Rect[]): OutlineSegments {
    // Normalize helper
    const r = normalizeRect(rect);

    type Edge =
        | { kind: "h"; y: number; x0: number; x1: number } // top/bottom (x runs)
        | { kind: "v"; x: number; y0: number; y1: number }; // left/right (y runs)

    const edges: Edge[] = [
        { kind: "h", y: r.y,       x0: r.x,       x1: r.x + r.w }, // top
        { kind: "v", x: r.x + r.w, y0: r.y,       y1: r.y + r.h }, // right
        { kind: "h", y: r.y + r.h, x0: r.x,       x1: r.x + r.w }, // bottom
        { kind: "v", x: r.x,       y0: r.y,       y1: r.y + r.h }, // left
    ];

    const dashed: Segment[] = [];
    const solid: Segment[] = [];

    for (const edge of edges) {
        // Edge span on its running axis
        const span = edge.kind === "h"
            ? [edge.x0, edge.x1] as [number, number]
            : [edge.y0, edge.y1] as [number, number];

        // Collect covered intervals along the edge's run
        const covered: Array<[number, number]> = [];

        for (const c0 of covers) {
            const c = normalizeRect(c0);

            if (edge.kind === "h") {
                // Cover must span this y to touch/cover the edge line
                if (c.y <= edge.y && edge.y <= c.y + c.h) {
                    const ov = intervalOverlap(span, [c.x, c.x + c.w]);
                    if (ov) covered.push(ov);
                }
            } else {
                // Vertical edge: cover must span this x
                if (c.x <= edge.x && edge.x <= c.x + c.w) {
                    const ov = intervalOverlap(span, [c.y, c.y + c.h]);
                    if (ov) covered.push(ov);
                }
            }
        }

        const mergedCovered = mergeIntervals(covered);
        const visible = subtractIntervals(span, mergedCovered);

        // Push dashed (covered) segments
        for (const [a, b] of mergedCovered) {
            if (almostEqual(a, b)) continue;
            dashed.push(edge.kind === "h"
                ? { x1: a, y1: edge.y, x2: b, y2: edge.y }
                : { x1: edge.x, y1: a, x2: edge.x, y2: b });
        }

        // Push solid (visible) segments
        for (const [a, b] of visible) {
            if (almostEqual(a, b)) continue;
            solid.push(edge.kind === "h"
                ? { x1: a, y1: edge.y, x2: b, y2: edge.y }
                : { x1: edge.x, y1: a, x2: edge.x, y2: b });
        }
    }

    return { solid, dashed };
}

/* ------------------------- helpers ------------------------- */

function normalizeRect(r: Rect): Rect {
    const x = Math.min(r.x, r.x + r.w);
    const y = Math.min(r.y, r.y + r.h);
    const w = Math.abs(r.w);
    const h = Math.abs(r.h);
    return { x, y, w, h };
}

function intervalOverlap(a: [number, number], b: [number, number]): [number, number] | null {
    const lo = Math.max(Math.min(a[0], a[1]), Math.min(b[0], b[1]));
    const hi = Math.min(Math.max(a[0], a[1]), Math.max(b[0], b[1]));
    return lo <= hi ? [lo, hi] : null; // inclusive so touching counts
}

function mergeIntervals(intervals: Array<[number, number]>): Array<[number, number]> {
    if (intervals.length === 0) return [];
    const sorted = intervals
        .map(([a, b]) => (a <= b ? [a, b] : [b, a]) as [number, number])
        .sort((i, j) => i[0] - j[0] || i[1] - j[1]);

    const out: Array<[number, number]> = [];
    let [cs, ce] = sorted[0];
    for (let k = 1; k < sorted.length; k++) {
        const [s, e] = sorted[k];
        if (s <= ce) {
            ce = Math.max(ce, e); // merge (inclusive)
        } else {
            out.push([cs, ce]);
            [cs, ce] = [s, e];
        }
    }
    out.push([cs, ce]);
    return out;
}

function subtractIntervals(
    full: [number, number],
    subs: Array<[number, number]>,
): Array<[number, number]> {
    const [F0, F1] = full[0] <= full[1] ? full : [full[1], full[0]];
    if (almostEqual(F0, F1)) return [];
    if (subs.length === 0) return [[F0, F1]];

    const merged = mergeIntervals(subs);
    const out: Array<[number, number]> = [];
    let cursor = F0;

    for (const [s, e] of merged) {
        const S = Math.max(F0, s);
        const E = Math.min(F1, e);
        if (cursor < S) out.push([cursor, S]);
        cursor = Math.max(cursor, E);
    }
    if (cursor < F1) out.push([cursor, F1]);
    return out;
}

function almostEqual(a: number, b: number, eps = 1e-9): boolean {
    return Math.abs(a - b) <= eps;
}
